import * as Sentry from '@sentry/browser';
import { SentryService } from '@bringg/frontend-utils';
import { IErrorNotifier } from '@bringg/frontend-utils/dist/services/airbrake/error-notifier';
import { getSharedRootStore } from '@bringg-frontend/global-stores';

import { AppConfig } from '../config';
import { getRootEnv } from '../create-root-env';

interface SentryInstanceType {
	getCurrentScope?: () => { setUser: (u: object) => void };
	configureScope?: (cb: (scope: { setUser: (u: object) => void }) => void) => void;
}
function applyUserToSentryScope(
	sentry: typeof Sentry | SentryInstanceType,
	user: { id?: number; merchant_id?: number }
): void {
	if (sentry.getCurrentScope) {
		sentry.getCurrentScope().setUser(user);
	} else if ((sentry as SentryInstanceType).configureScope) {
		(sentry as SentryInstanceType).configureScope(scope => scope.setUser(user));
	}
}

export class SentryNotifier {
	private notifier!: IErrorNotifier;

	constructor() {
		void this.init();
	}

	async init() {
		const envConfig = await AppConfig.getEnvironmentConfig();
		const { environment, sentry_dsn } = envConfig;
		const didSentryAlreadyInstrumentedElsewhere =
			window.firstSentryInitSource !== undefined && window.firstSentryInitSource !== 'react';

		if (sentry_dsn) {
			this.notifier = new SentryService({
				dsn: sentry_dsn,
				environment: environment,
				integrations: didSentryAlreadyInstrumentedElsewhere ? [] : undefined
			});

			window.firstSentryInitSource = window.firstSentryInitSource || 'react';
		} else {
			this.notifier = undefined!;

			window.firstSentryInitSource =
				window.firstSentryInitSource === 'react' ? undefined : window.firstSentryInitSource;
		}
	}

	/**
	 * Set user_id and merchant_id on Sentry's global scope so all events
	 * (including automatically captured errors) include user context.
	 * Call this after the root store is initialized (e.g. from the React app bootstrap).
	 * @see https://docs.sentry.io/platforms/javascript/enriching-events/scopes/
	 */

	public setUserOnScope(): void {
		const { currentUser } = getSharedRootStore().data.usersStore;
		const user_id = currentUser?.id;
		const merchant_id = currentUser?.merchant_id;
		if (user_id === undefined && merchant_id === undefined) return;

		const user: { id?: number; merchant_id?: number } = {};
		if (user_id !== undefined) user.id = user_id;
		if (merchant_id !== undefined) user.merchant_id = merchant_id;

		// Set on window.Sentry (CDN client) for events captured by Angular / global script
		// @ts-expect-error - window.Sentry is not defined in the global scope
		const globalSentry = typeof window !== 'undefined' ? window.Sentry : undefined;
		if (globalSentry) {
			applyUserToSentryScope(globalSentry, user);
		}

		// Set on npm Sentry (same instance frontend-utils uses) so React-captured events show User
		if (typeof Sentry !== 'undefined') {
			applyUserToSentryScope(Sentry, user);
		}
	}

	// for tests
	reset() {
		this.notifier = undefined!;
	}

	public notify(error: Error, params = {}, context = null) {
		if (this.notifier) {
			const { currentUser } = getSharedRootStore().data.usersStore;
			const { environment } = getRootEnv().envConfig;
			const user_id = currentUser?.id;
			const merchant_id = currentUser?.merchant_id;

			this.notifier.notify({
				error,
				params: {
					...params,
					user_id,
					merchant_id,
					environment
				},
				context
			});
		}
	}
}

export const sentryNotifier = new SentryNotifier();
